'use client';

import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { ThemeSearchCardProps } from '@/components/organisms/theme-search-card/theme-search-card';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useLockScroll from '@/libraries/hooks/use-lock-scroll';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import MainNavigationLarge from './main-navigation-large/main-navigation-large';
import MainNavigationSmall from './main-navigation-small/main-navigation-small';
import styles from './main-navigation.module.scss';

export interface MainNavigationMenuItem {
  /** The campaign teaser to show in the dropdown */
  campaignTeaser?: (ThemeSearchCardProps & { title: string }) | null;
  /** The number to show as a count for the menu item */
  count?: number;
  /** The text to show as a description for the menu item in dropdowns */
  description?: string;
  /** Whether to show the menu count */
  hideNumbers?: boolean;
  /** The icon to show on the menu item */
  icon?: IconName;
  /** The id of the menu item */
  id?: string;
  /** Indicates whether the menu item is a featured item */
  isFeatured?: boolean;
  /** Indicates whether the menu item is a priority item */
  isPriority?: boolean;
  /** The list of items to show under the menu item */
  items?: MainNavigationMenuItem[];
  /** The layout to use for the menu item */
  layout?: Content.Components['NavigationRootItemLayout'];
  /** The link of the menu item */
  link: Content.Properties.Link;
  /** The masonry settings to use for the menu item */
  masonrySettings?: Content.Components['MasonrySettingsDto'] | null;
  /** The type of the menu item */
  type?: 'default' | 'favorites' | 'search' | 'user';
}

export interface MainNavigationProps {
  /** The link to use for booking */
  bookingLink: Content.Properties.Link | null;
  /** The sub brand navigation items*/
  brandNavigation?: Content.Properties.BrandNavigation | null;
  /** The class name to apply */
  className?: string;
  /** The link to use for content search */
  contentSearchPageLink: Content.Properties.Link | null;
  /** The link to use for favorites */
  favoritesPageLink: Content.Properties.Link | null;
  /** The link to use for the logo */
  homePageLink: Content.Properties.Link | null;
  /** An array of links to show */
  menuItems: MainNavigationMenuItem[];
  /** An array of profile menu items */
  profileMenu: Content.Properties.ProfileMenu | null;
  /** An array of quick links to show */
  quickAccessItems?: MainNavigationMenuItem[];
  /** An array of sub brand items to switch context */
  subBrandNavigations?: Content.Properties.SubBrandNavigation[];
}

interface NavigationContentParams {
  bookingLink: Content.Properties.Link | null;
  brand: Content.Properties.Brand;
  brandNavigationItems: Content.Properties.BrandNavigationItem[];
  contentSearchPageLink: Content.Properties.Link | null;
  favoritesPageLink: Content.Properties.Link | null;
  homePageLink: Content.Properties.Link | null;
  isVisible: boolean;
  menuItems: MainNavigationMenuItem[];
  profileMenu: Content.Properties.ProfileMenu | null;
  quickAccessItems: MainNavigationMenuItem[];
}

const MainNavigation = ({
  bookingLink,
  brandNavigation,
  className,
  contentSearchPageLink,
  favoritesPageLink,
  homePageLink,
  menuItems = [],
  profileMenu,
  quickAccessItems = [],
  subBrandNavigations = [],
}: MainNavigationProps) => {
  const { t } = useI18n();
  const { setIsLocked } = useLockScroll();
  const isLaptop = useWindowSize('laptop');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  const [isSubBrandsMenuOpen, setIsSubBrandsMenuOpen] = useState(false);
  const { brand } = usePageDetailsContext();

  usePageTransition({
    onEnter: () => {
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
      setIsQuickMenuOpen(false);
      setIsSubBrandsMenuOpen(false);
    },
  });

  useEffect(() => {
    if (isMenuOpen) {
      setIsProfileMenuOpen(false);
      setIsQuickMenuOpen(false);
      setIsSubBrandsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuOpen]);

  useEffect(() => {
    if (isQuickMenuOpen) {
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
      setIsSubBrandsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuickMenuOpen]);

  useEffect(() => {
    if (isSubBrandsMenuOpen) {
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
      setIsQuickMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubBrandsMenuOpen]);

  useEffect(() => {
    if (isProfileMenuOpen) {
      setIsMenuOpen(false);
      setIsQuickMenuOpen(false);
      setIsSubBrandsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (
      isMenuOpen ||
      isQuickMenuOpen ||
      isSubBrandsMenuOpen ||
      isProfileMenuOpen
    ) {
      setIsLocked(true);
    } else {
      setIsLocked(false);
    }

    return () => {
      setIsLocked(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuOpen, isQuickMenuOpen, isSubBrandsMenuOpen, isProfileMenuOpen]);

  const renderNavigationContent = ({
    bookingLink,
    brand,
    brandNavigationItems,
    contentSearchPageLink,
    favoritesPageLink,
    homePageLink,
    isVisible,
    menuItems,
    profileMenu,
    quickAccessItems,
  }: NavigationContentParams) => {
    return (
      <React.Fragment>
        {isLaptop ? (
          <MainNavigationLarge
            bookingLink={bookingLink}
            brand={brand}
            brandNavigationItems={brandNavigationItems}
            className={className}
            contentSearchPageLink={contentSearchPageLink}
            favoritesPageLink={favoritesPageLink}
            homePageLink={homePageLink}
            isMenuOpen={isMenuOpen}
            isProfileMenuOpen={isProfileMenuOpen}
            isQuickMenuOpen={isQuickMenuOpen}
            isSubBrandsMenuOpen={isSubBrandsMenuOpen}
            isVisible={isVisible}
            menuItems={menuItems}
            profileMenu={profileMenu}
            setIsMenuOpen={setIsMenuOpen}
            setIsProfileMenuOpen={setIsProfileMenuOpen}
            setIsQuickMenuOpen={setIsQuickMenuOpen}
            setIsSubBrandsMenuOpen={setIsSubBrandsMenuOpen}
          />
        ) : (
          <MainNavigationSmall
            bookingLink={bookingLink}
            brand={brand}
            brandNavigationItems={brandNavigationItems}
            className={className}
            contentSearchPageLink={contentSearchPageLink}
            favoritesPageLink={favoritesPageLink}
            homePageLink={homePageLink}
            isMenuOpen={isMenuOpen}
            isSubBrandsMenuOpen={isSubBrandsMenuOpen}
            isVisible={isVisible}
            menuItems={menuItems}
            profileMenu={profileMenu}
            quickAccessItems={quickAccessItems}
            setIsMenuOpen={setIsMenuOpen}
            setIsSubBrandsMenuOpen={setIsSubBrandsMenuOpen}
          />
        )}
      </React.Fragment>
    );
  };

  const activeSubBrand = subBrandNavigations.find(
    subBrand => subBrand.brandName === brand,
  );

  const activeNavigationContent = activeSubBrand
    ? {
        bookingLink: activeSubBrand.bookingLink,
        brand: activeSubBrand.brandName as Content.Properties.Brand,
        brandNavigationItems: activeSubBrand.brandNavigation?.items || [],
        contentSearchPageLink,
        favoritesPageLink: activeSubBrand.favoritesPageLink,
        homePageLink: activeSubBrand.homePageLink,
        isVisible: true,
        menuItems: activeSubBrand.items,
        profileMenu: activeSubBrand.profileMenu,
        quickAccessItems: activeSubBrand.quickAccessItems,
      }
    : {
        bookingLink,
        brand: 'default' as Content.Properties.Brand,
        brandNavigationItems: brandNavigation?.items || [],
        contentSearchPageLink,
        favoritesPageLink,
        homePageLink,
        isVisible: true,
        menuItems,
        profileMenu,
        quickAccessItems: quickAccessItems || [],
      };

  return (
    <React.Fragment>
      <button
        aria-label={t('items.common.words.close')}
        className={classNames(styles.component, {
          [styles.isMenuOpen]:
            isMenuOpen ||
            isProfileMenuOpen ||
            isQuickMenuOpen ||
            isSubBrandsMenuOpen,
        })}
        onClick={() => {
          setIsMenuOpen(false);
          setIsProfileMenuOpen(false);
          setIsQuickMenuOpen(false);
          setIsSubBrandsMenuOpen(false);
        }}
        type="button"
      />

      {renderNavigationContent(activeNavigationContent)}
    </React.Fragment>
  );
};

MainNavigation.displayName = 'MainNavigation';

export default MainNavigation;
